import { useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { TabBar } from './TabBar';
import { DesktopHeader } from './DesktopHeader';
import { PwaToasts } from '../domain/PwaToasts';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';
import { apiGet } from '../../lib/api';
import type { AnnouncementResponse, HealthResponse } from '@shared/types';

interface AppShellProps {
  children: ReactNode;
  showTabBar?: boolean;
}

// Part 14 §F10 — show a top banner after the server fails to respond
// to /api/health three times in a row. Banner clears as soon as one
// successful response lands. We poll every 5 minutes to keep the cost
// negligible (12 hits/hour/user) while still catching outages quickly.
const HEALTH_POLL_MS = 5 * 60 * 1000;
const HEALTH_FAILURE_THRESHOLD = 3;

// Part 14 §F11 — admin announcement banner. We refresh on the same
// cadence as the health poll so a freshly-published announcement
// appears within five minutes without extra request volume.
export function AppShell({ children, showTabBar = true }: AppShellProps) {
  const online = useOnlineStatus();
  const location = useLocation();
  const { pending } = useOfflineQueue();
  const [serverDown, setServerDown] = useState(false);
  const [announcement, setAnnouncement] = useState<
    AnnouncementResponse['announcement']
  >(null);

  useEffect(() => {
    // Track consecutive failures so a single blip doesn't flash the
    // banner. The state-machine is small enough that a local counter
    // (not state) keeps the render path quiet.
    let failures = 0;
    let cancelled = false;

    async function pollHealth() {
      try {
        await apiGet<HealthResponse>('/api/health');
        if (cancelled) return;
        failures = 0;
        setServerDown(false);
      } catch {
        if (cancelled) return;
        failures += 1;
        if (failures >= HEALTH_FAILURE_THRESHOLD) {
          setServerDown(true);
        }
      }
    }

    async function pollAnnouncement() {
      try {
        const res = await apiGet<AnnouncementResponse>('/api/announcement');
        if (cancelled) return;
        setAnnouncement(res.announcement ?? null);
      } catch {
        // 401 (guest) or network blip — leave the existing banner state
        // alone; we don't want a transient guest visit to wipe an active
        // banner the moment the next authed render lands.
      }
    }

    void pollHealth();
    void pollAnnouncement();
    const id = window.setInterval(() => {
      void pollHealth();
      void pollAnnouncement();
    }, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Move focus into the new page on every route change so screen-reader
  // and keyboard users are oriented rather than stranded on <body>
  // (WCAG 2.4.3). Prefer the page's <h1>; fall back to the <main>
  // landmark for a screen that hasn't rendered a heading yet.
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return;
    const heading = main.querySelector('h1');
    const target = heading instanceof HTMLElement ? heading : main;
    target.setAttribute('tabindex', '-1');
    target.focus();
  }, [location.pathname]);

  return (
    <div className="app-shell">
      {/* Skip-to-content link — visible only on focus, lets keyboard
          users bypass the offline strip + tabbar nav. CSS in globals.css. */}
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      {announcement ? (
        <AnnouncementBanner
          message={announcement.message}
          kind={announcement.kind}
        />
      ) : null}
      {serverDown ? <ServerDownBanner /> : null}
      {/* DesktopHeader is always rendered; CSS hides it below 960px.
          TabBar mirrors the inverse via @media. */}
      <DesktopHeader />
      <PwaToasts />
      {!online ? (
        <div className="offline-strip" role="status">
          {pending > 0
            ? `Offline · ${pending} change${pending === 1 ? '' : 's'} saved — will sync when you're back`
            : "Offline · changes will sync when you're back"}
        </div>
      ) : null}
      <main id="main-content" className="app-main">
        <div className="container">{children}</div>
      </main>
      {showTabBar ? <TabBar /> : null}
      {/* Phase 8 — the global SOS toolkit FAB was removed in favour of
          the embedded SosToolkitCard on Today (see
          src/components/today/SosToolkitCard.tsx). Users who want to
          hide the card entirely can do so in Settings → Help &
          appearance → "Show SOS toolkit on Today." */}
    </div>
  );
}

// Borrowed from PwaToasts' update-ready banner. Same fixed-top
// position so a single banner stack stays visually consistent.
const BANNER_BASE: CSSProperties = {
  position: 'fixed',
  insetBlockStart: 'env(safe-area-inset-top, 0px)',
  insetInlineStart: 0,
  insetInlineEnd: 0,
  zIndex: 60,
  padding: 'var(--space-2) var(--space-3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-3)',
  fontSize: 'var(--text-caption)',
};

function ServerDownBanner() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        ...BANNER_BASE,
        background: 'var(--color-warning, #b45309)',
        color: 'var(--color-bg-canvas)',
      }}
    >
      <span>We're having trouble reaching the server.</span>
    </div>
  );
}

function AnnouncementBanner({
  message,
  kind,
}: {
  message: string;
  kind: 'info' | 'warn' | 'critical';
}) {
  // Use the existing palette tokens — `--color-primary` for info matches
  // the PwaToasts update banner; warn/critical fall back to amber/red.
  const bg =
    kind === 'critical'
      ? 'var(--color-danger, #b91c1c)'
      : kind === 'warn'
        ? 'var(--color-warning, #b45309)'
        : 'var(--color-primary)';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        ...BANNER_BASE,
        background: bg,
        color: 'var(--color-bg-canvas)',
      }}
    >
      <span>{message}</span>
    </div>
  );
}
